import React, { useEffect, useState } from 'react';
import { isMuted, playHover, playSelect, setMuted, subscribe } from '../../utils/sound';
import '../PlacementQuiz.css';

/**
 * Shared quiz presentation atoms (ROO-179).
 *
 * One source of truth for the portal's quiz look. PlacementQuiz and
 * TestTaking (and later the unified QuizEngine, ROO-180) all render THESE,
 * so the visual language can't fork again. Styling comes from the existing
 * pq-* classes in PlacementQuiz.css (imported here so any consumer gets it).
 */

const ArrowLeft = (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M19 12H5m6 6-6-6 6-6" />
  </svg>
);

const SpeakerOn = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
    <path d="M16 8.5a5 5 0 0 1 0 7" />
    <path d="M18.5 6a8.5 8.5 0 0 1 0 12" />
  </svg>
);

const SpeakerOff = (
  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor"
    strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M11 5 6 9H3v6h3l5 4V5Z" />
    <path d="m17 9 5 6M22 9l-5 6" />
  </svg>
);

/**
 * Sound mute toggle (ROO-233) — shown on every quiz surface. Reflects the
 * shared mute state live via subscribe(), so flipping it in one tab of the
 * app updates every other rendered toggle immediately. Kid-sized 44px+
 * touch target per the portal's accessibility bar.
 */
export const SoundMuteToggle: React.FC<{ className?: string; style?: React.CSSProperties }> = ({ className, style }) => {
  const [muted, setMutedState] = useState(isMuted());
  useEffect(() => subscribe(setMutedState), []);
  return (
    <button
      type="button"
      className={`pq-bar__mute${className ? ` ${className}` : ''}`}
      style={style}
      onClick={() => setMuted(!muted)}
      aria-label={muted ? 'Unmute sound effects' : 'Mute sound effects'}
      aria-pressed={muted}
      title={muted ? 'Unmute sound effects' : 'Mute sound effects'}
    >
      {muted ? SpeakerOff : SpeakerOn}
    </button>
  );
};

/** Top bar: cancel/exit on the left, title, free-form meta on the right, mute toggle always. */
export const QuizTopBar: React.FC<{
  title: string;
  onCancel?: () => void;
  cancelLabel?: string;
  right?: React.ReactNode;
  cancelDisabled?: boolean;
}> = ({ title, onCancel, cancelLabel = 'Cancel', right, cancelDisabled }) => (
  <header className="pq-bar">
    {onCancel && (
      <button className="pq-bar__back" type="button" onClick={onCancel} disabled={cancelDisabled}>
        {ArrowLeft} {cancelLabel}
      </button>
    )}
    <span className="pq-bar__title">{title}</span>
    {right && <span className="pq-bar__progress">{right}</span>}
    <SoundMuteToggle style={right ? undefined : { marginLeft: 'auto' }} />
  </header>
);

/** Thin progress strip under the top bar. */
export const QuizProgressStrip: React.FC<{ pct: number }> = ({ pct }) => (
  <div className="pq-strip">
    <div className="pq-strip__fill" style={{ width: `${Math.max(0, Math.min(100, pct))}%` }} />
  </div>
);

export interface QuizOptionFeedback {
  /** this option is a correct one (revealed) */
  isCorrectOption: boolean;
  /** the student picked this option and it's wrong */
  isWrongPick: boolean;
}

/** One answer option — pill button with selected + revealed-feedback states. */
export const QuizOption: React.FC<{
  selected: boolean;
  multi?: boolean;
  onClick: () => void;
  feedback?: QuizOptionFeedback | null;
  img?: string | null;
  /** pre-rendered rich-text HTML for the label (takes priority over text) */
  html?: string | null;
  text?: string;
  /** 1-based position, used to label image-only options for screen readers */
  index?: number;
}> = ({ selected, multi, onClick, feedback, img, html, text, index }) => {
  // Image-only options have no text label; give the button + image an
  // accessible name so screen-reader users don't hear an unlabeled control (ROO-192).
  const accessibleLabel = text || (index != null ? `Option ${index}` : 'Answer option');
  const fbStyle: React.CSSProperties | undefined = feedback
    ? feedback.isCorrectOption
      ? { borderColor: '#5DAA3A', background: '#DCEFCC' }
      : feedback.isWrongPick
      ? { borderColor: '#E2765A', background: '#FAD5C8' }
      : { opacity: 0.65 }
    : undefined;
  return (
    <button
      type="button"
      role={multi ? 'checkbox' : 'radio'}
      aria-checked={selected}
      aria-label={!text ? accessibleLabel : undefined}
      className={`pq-option${selected ? ' is-selected' : ''}${multi ? ' is-multi' : ''}`}
      style={fbStyle}
      onClick={() => { playSelect(); onClick(); }}
      onMouseEnter={playHover}
      onFocus={playHover}
    >
      <span className="pq-option__radio" aria-hidden="true" />
      {img && (
        <span className="pq-option__img"><img src={img} alt={accessibleLabel} /></span>
      )}
      {html ? (
        <span className="pq-option__label" dangerouslySetInnerHTML={{ __html: html }} />
      ) : text ? (
        <span className="pq-option__label">{text}</span>
      ) : null}
      {feedback?.isCorrectOption && <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#14593C' }}>✓</span>}
      {feedback?.isWrongPick && <span style={{ marginLeft: 'auto', fontWeight: 800, color: '#B83E24' }}>✗</span>}
    </button>
  );
};

export interface PaletteItem {
  id: string;
  answered: boolean;
  flagged: boolean;
}

/** Exam question palette: jump-anywhere grid with answered/flagged/current states. */
export const QuestionPalette: React.FC<{
  items: PaletteItem[];
  currentIndex: number;
  onJump: (index: number) => void;
}> = ({ items, currentIndex, onJump }) => (
  <div data-testid="question-palette" style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
    {items.map((it, i) => {
      const isCurrent = i === currentIndex;
      return (
        <button
          key={it.id}
          type="button"
          onClick={() => onJump(i)}
          aria-label={`Question ${i + 1}${it.answered ? ', answered' : ''}${it.flagged ? ', flagged' : ''}`}
          style={{
            width: 34, height: 34, borderRadius: 10, fontSize: 13, fontWeight: 700,
            cursor: 'pointer', position: 'relative',
            border: isCurrent ? '2px solid #14593C' : '1.5px solid #E5DCC3',
            background: it.answered ? '#DCEFCC' : '#fff',
            color: it.answered ? '#14593C' : '#6B7A6F',
          }}
        >
          {i + 1}
          {it.flagged && (
            <span style={{ position: 'absolute', top: -4, right: -4, width: 10, height: 10, borderRadius: '50%', background: '#E2903A' }} />
          )}
        </button>
      );
    })}
  </div>
);

// ── shared shell primitives (ROO-180 phase 2) ────────────────────────────────

/** Bottom navigation row: back/flag controls left, primary action right. */
export const QuizNavBar: React.FC<{ left?: React.ReactNode; right?: React.ReactNode; style?: React.CSSProperties }> =
  ({ left, right, style }) => (
    <div className="pq-nav" style={style}>
      <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>{left}</div>
      {right}
    </div>
  );

export const QuizBackButton: React.FC<{ onClick: () => void; disabled?: boolean; children?: React.ReactNode; style?: React.CSSProperties }> =
  ({ onClick, disabled, children = '← Back', style }) => (
    <button className="pq-nav__back" type="button" onClick={onClick} disabled={disabled} style={style}>
      {children}
    </button>
  );

export const QuizPrimaryButton: React.FC<{ onClick: () => void; disabled?: boolean; finish?: boolean; children: React.ReactNode }> =
  ({ onClick, disabled, finish, children }) => (
    <button
      className={`pq-nav__next${finish ? ' pq-nav__next--finish' : ''}`}
      type="button"
      onClick={onClick}
      disabled={disabled}
    >
      {children}
    </button>
  );
